import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import { useDepartment } from '../contexts/DepartmentContext';

import { APPROVAL_ROLE_LABELS as ROLE_LABELS } from '../constants/statuses';
import { formatDateTimeCompact } from '../utils/dateFormat';
import { collapsibleStepCountFrom, approveButtonLabel } from '../utils/approvalStepCollapse';
import { countDelivery } from '../utils/deliveryCountableItems';
import { isDifferentApprover } from '../utils/orderStepInfo';
import ApproveOnBehalfDialog from './ApproveOnBehalfDialog';
import { DESKTOP_SECTION_CARD, DESKTOP_SECTION_CHEVRON, DESKTOP_SECTION_TITLE, DESKTOP_SECTION_HEAD_BLEED } from './OrderSectionHeader';

/**
 * Colours for the step/stage indicator and the outcome word.
 *
 * \uD83D\uDD11 THIS IS NO LONGER KEYED BY THE RAW DB `status` COLUMN ALONE (2026-08-18,
 * order-lifecycle stepper). `order_approval_steps.status` only ever holds
 * `pending`/`approved`/`rejected`/`skipped`/`overridden`, and `pending` used to
 * mean two different things a viewer needs told apart: "this is what you are
 * waiting on right now" and "this has not been reached yet". `deriveStepView`
 * below computes a DISPLAY status \u2014 `awaiting` / `not_reached` / `cancelled` /
 * `partial` are new keys that do not exist in the database \u2014 and every
 * lookup into this map goes through that derived value, never through
 * `step.status` directly. Overall-header lookups (`approval.overall_status`)
 * still use the raw value, which is why `pending`/`approved`/`rejected`/
 * `overridden` are still present unchanged.
 *
 * \uD83E\uDEA4 THE `dark:` SITUATION HERE IS NOT WHAT IT LOOKS LIKE \u2014 READ BEFORE "FIXING".
 * Almost every class below is a BARE light-mode class with no `dark:` pair, and
 * that is deliberate rather than an omission: `client/src/index.css` carries a
 * table of GLOBAL `html.dark \u2026 !important` overrides that already deliver the
 * dark value for each of them, and an `!important` author declaration beats any
 * non-important rule whatever its specificity. So a local `dark:` pair on these
 * would be compiled, shipped, and INERT \u2014 which is worse than none, because it
 * reads as explicit dark support and stops the next person checking.
 *
 *   bg-yellow-100  \u2192 index.css:382   bg-green-100 \u2192 :374   bg-red-100 \u2192 :378
 *   bg-gray-100    \u2192 index.css:90    bg-orange-100 \u2192 :390
 *   text-yellow-700\u2192 index.css:499   text-green-700 \u2192 :483  text-red-700 \u2192 :487
 *   text-gray-500  \u2192 index.css:260
 *
 * \u26A0\uFE0F `text-orange-700` is THE ONE EXCEPTION \u2014 index.css has no rule for it (only
 * `[data-section-title].bg-orange-50` at :205 mentions orange at all on that
 * axis), so nothing delivers a dark value and orange text would render as
 * #c2410c on a dark surface. `dark:text-orange-300` therefore genuinely WINS
 * here, and every orange entry below (overridden / awaiting / partial \u2014 the
 * three "in progress, not yet decided" states) carries it for that reason,
 * reusing the exact pair `overridden` already proved safe rather than
 * inventing a second one.
 *
 * \uD83D\uDD34 The `border` key that used to sit on every entry is GONE. Its only consumer
 * was the component's outer card, which no longer takes a status tint \u2014 the
 * overall status is stated in WORDS by the header badge, so a whole-block fill
 * and a coloured edge were a third and fourth way of saying it.
 *
 * Glyphs are a NON-COLOUR CHANNEL, never decoration \u2014 `approved`/`rejected`
 * both keep a distinct glyph from `cancelled`/`not_reached` even where two
 * share a background, and the STATUS WORD beside each is the sole-carrier
 * requirement's second line of defence. `awaiting` and `not_reached` show the
 * step's own NUMBER instead of a glyph (matching the pre-existing convention
 * for anything not yet decided) \u2014 see `renderStepBadgeContent` below.
 */
const STATUS_CONFIG = {
  // Raw `order_approvals.overall_status` values \u2014 header badge only.
  pending: { bg: 'bg-orange-100', text: 'text-orange-700 dark:text-orange-300', label: 'Pending', icon: '\u25CF' },
  approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved', icon: '\u2713' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected', icon: '\u2717' },
  overridden: { bg: 'bg-orange-100', text: 'text-orange-700 dark:text-orange-300', label: 'Overridden', icon: '\u26A1' },
  // Raw `order_approval_steps.status` value with no derived meaning \u2014 a
  // decided step's own row.
  skipped: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Skipped', icon: '\u2192' },
  // DERIVED display statuses (never a raw DB value) \u2014 see `deriveStepView`.
  awaiting: { bg: 'bg-orange-100', text: 'text-orange-700 dark:text-orange-300', label: 'Awaiting', icon: '\u25CF' },
  not_reached: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Not yet', icon: '\u25CB' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled', icon: '\u2298' },
  partial: { bg: 'bg-orange-100', text: 'text-orange-700 dark:text-orange-300', label: 'Partially received', icon: '\u25D0' },
  // A completed LIFECYCLE STAGE (Ordered/Delivered) \u2014 same green as `approved`
  // (genuinely the same fact: this has happened), a DIFFERENT label on
  // purpose. "Approved" is a claim about a sign-off; a stage is not signed
  // off, it is reached \u2014 reusing the word would read as "Ordered Approved".
  done: { bg: 'bg-green-100', text: 'text-green-700', label: 'Done', icon: '\u2713' },
};

/**
 * \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
 * THE LIFECYCLE VIEW MODEL \u2014 derived ONCE, fed to BOTH render branches
 * \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
 *
 * Added 2026-08-18. Product owner, on being shown a screenshot of a one-step
 * workflow that goes silent after approval: *"ah not just approvals but,
 * ordereed, delivered etc"* \u2014 the panel must show the WHOLE order lifecycle,
 * not only the approval chain. `buildLifecycleEntries` is the ONE place that
 * decides colour/label/current-ness for every row in BOTH the mobile stacked
 * list and the desktop horizontal row; this file's own docblock elsewhere
 * warns the two layouts "CANNOT disagree about status/current/hidden-step
 * handling" \u2014 the fix is to give them nothing to disagree about.
 *
 * \uD83D\uDEA8 THE FINAL HIDDEN STEP IS NOW RENDERED, NOT INVENTED. `approval.steps` is
 * consumed WHOLE \u2014 no `.filter(s => !s.is_hidden)` \u2014 because
 * `withFinalApproverStep` (`server/helpers/approvalChainFinalStep.js`) already
 * appends the department-approver step; this file used to filter it out for
 * DISPLAY only. `is_hidden` remains exactly as load-bearing as ever for
 * ROUTING (`/initiate`'s validation loop, the chain builders) \u2014 nothing here
 * touches that column's meaning, only whether the client draws the row it
 * always had.
 *
 * \u26A0\uFE0F THE OLD COMPENSATION THIS REPLACED, AND WHY IT WAS DANGEROUS ONCE VISIBLE.
 * The component used to carry:
 *   const isAtHiddenStep = currentStep && currentStep.is_hidden;
 *   const effectiveStatus = isAtHiddenStep && step.status === 'pending'
 *     ? 'approved' : step.status;
 * That was harmless ONLY because the hidden step was filtered out of the loop
 * it ran inside \u2014 every VISIBLE step still in `pending` at that point had
 * already been decided in truth (the chain cannot be sitting AT the final
 * step with an earlier one undecided), so the override was a no-op that
 * happened to never fire. The moment the final step is rendered too, it is
 * the ONE step this override WOULD fire on \u2014 the current, genuinely
 * undecided step is 'pending' by definition \u2014 and it would print "\u2713 Approved"
 * on a placement nobody has confirmed. **Deleted outright, not narrowed.**
 * `isAtHiddenStep` survives only for the ACTIONS block's copy (button label,
 * "ready to place" prompt), which was never the lying part.
 *
 * \uD83D\uDD11 "IS THIS ORDER STILL LIVE" GOVERNS WHETHER A PENDING STEP MAY EVER READ AS
 * "AWAITING" (orange). A step's raw `status` cannot distinguish "the one
 * everyone is waiting on right now" from "not reached yet" \u2014 both are
 * `pending` \u2014 and once the order stops being `submitted` neither reading is
 * true any more: `approved`/`ordered`/`partially_delivered`/`delivered` mean
 * every step is genuinely decided (a lingering `pending` there is stale data,
 * never a live wait), and `rejected`/`cancelled` mean the chain is dead. So:
 *   isLive  = orderStatus === 'submitted'
 * A pending step is `awaiting` (orange) only when isLive AND it is the
 * current step; every other pending step \u2014 future ones on a live order,
 * ANY pending step on a dead one \u2014 is `not_reached` (grey). This is what
 * closes the measured production inconsistency (cancelled orders with 10
 * stray `pending` steps where 1,581 got bulk-`skipped`): the renderer never
 * trusts raw `pending` to mean "awaiting" outside a live order, so the two
 * stored outcomes for the same situation render IDENTICALLY (grey) whichever
 * one a given row happens to carry.
 *
 * \uD83D\uDD11 CANCELLATION \u2014 "ONLY THE STAGE WHERE THE ORDER ACTUALLY DIED GOES RED."
 * `PATCH /:id/cancel` (`server/routes/orders.js`) does NOT mark any step
 * `rejected`; it sets `order_approvals.overall_status = 'rejected'` (reusing
 * the same value a real rejection uses) and bulk-`skip`s whatever was
 * `pending`. So there is no per-step DB fact saying "this is where it died" \u2014
 * the renderer has to infer it, and it does so from `orders.status_before_
 * cancel` (passed in as `statusBeforeCancel`), which records how far the
 * order had got:
 *   'submitted'            \u2192 died mid-APPROVAL: the step that was `current`
 *                             at cancel time (by `approval.current_step`,
 *                             REGARDLESS of whether its own row ended up
 *                             `pending` or `skipped` \u2014 current_step is not
 *                             touched by cancel, so it still names the right
 *                             step under either stored outcome) \u2192 `cancelled`
 *   'approved'              \u2192 all approvals genuinely done; died before being
 *                             marked Ordered \u2192 the ORDERED stage \u2192 `cancelled`
 *   'ordered'/'partially_delivered'
 *                            \u2192 died before Delivered \u2192 the DELIVERED stage
 *                              \u2192 `cancelled`
 *   anything else / absent  \u2192 no red marker is fabricated anywhere; every
 *                             stage renders from `orderStatus` alone. Never
 *                             guess where an order without this field died.
 * A cancelled order therefore carries AT MOST ONE red entry, matching the
 * ruling's "must not look like four people rejected it when nobody did".
 *
 * \uD83D\uDD11 REJECTION needs none of the above \u2014 `/reject` genuinely marks ONE step's
 * own `status = 'rejected'`, so the red entry is read straight off real data,
 * never inferred from `current_step`.
 *
 * \uD83D\uDD11 TIMESTAMPS \u2014 Ordered/Delivered carry NONE, deliberately. There is no
 * `ordered_at`/`delivered_at` column (verified against both schema files);
 * the brief's own instruction is "timestamps come from the audit log", and
 * `OrderExpandedDetails.js`'s docblock counts this panel's requests at
 * exactly one (`GET /api/approvals/orders/:id`) with the audit log LAZY and
 * fetched only on first open of the history disclosure \u2014 adding an eager
 * audit-log fetch here to back-fill two dates would break that invariant for
 * every expand, most of which nobody drills into. "Prefer deriving from data
 * already fetched" (the brief's own words) means: no date on these two rows,
 * not a second request. Approval-step timestamps are UNCHANGED \u2014 they come
 * from `step.decided_at`, already on the payload this component already
 * fetches.
 */

/** Real per-department-approver approval STEP \u2192 one lifecycle entry. */
function deriveStepView(step, { isLive, isDying, currentStepOrder }) {
  const raw = step.status; // 'pending' | 'approved' | 'rejected' | 'skipped' | 'overridden'
  let displayStatus;
  // 🚨 ORDER MATTERS: `isDying` (the step that was current when a live order
  // was cancelled) is checked BEFORE `raw === 'skipped'`, deliberately — the
  // measured production inconsistency is that THIS EXACT SITUATION stores
  // TWO different raw outcomes for the current step (`pending`, when
  // `PATCH /:id/cancel`'s bulk-skip UPDATE silently failed, vs `skipped`,
  // the normal case), and both must render IDENTICALLY as `cancelled`. A
  // `skipped`-before-`isDying` ordering would let the two stored outcomes
  // disagree on screen for the one row this file exists to make agree.
  // `approved`/`rejected`/`overridden` are never overridden by `isDying` —
  // those are real decided facts about a step that already happened and
  // must never be recoloured because something ELSE died later.
  if (raw === 'approved') displayStatus = 'approved';
  else if (raw === 'rejected') displayStatus = 'rejected';
  else if (raw === 'overridden') displayStatus = 'overridden';
  else if (isDying && Number(step.step_order) === Number(currentStepOrder)) displayStatus = 'cancelled';
  else if (raw === 'skipped') displayStatus = 'skipped';
  else if (isLive && Number(step.step_order) === Number(currentStepOrder)) displayStatus = 'awaiting';
  else displayStatus = 'not_reached';

  const mismatch = (step.status === 'approved'
    && step.approved_by_user_id != null
    && step.approved_by_user_id !== step.approver_user_id)
    ? (step.approved_by_name || 'another approver')
    : null;

  // The hidden final step that confirmed ITSELF (2026-10-09): the SERVER states it
  // (`auto_confirmed`). A NULL actor alone is not proof: pre-08-14 hand approvals,
  // deleted approvers and restores leave the same shape.
  const autoConfirmed = !!step.is_hidden
    && Number(step.auto_confirmed) === 1;

  return {
    key: `step-${step.id}`,
    kind: 'approval',
    stepOrder: step.step_order,
    displayStatus,
    primary: autoConfirmed ? 'Ready to order' : step.approver_name,
    // The hidden step is the DEPARTMENT's approver whatever role it is stamped
    // with; the shared label for 'admin' reads Admin/Buyer, which misleads here.
    roleLabel: step.is_hidden
      ? 'Department buyer'
      : (ROLE_LABELS[step.approver_role] || step.approver_role),
    autoConfirmed,
    role: step.approver_role,
    timestamp: step.decided_at || null,
    isCurrent: displayStatus === 'awaiting',
    // The auto-confirm note is said by the "confirmed automatically" text instead.
    // 🔀 A REJECTING step's note IS the rejection reason, which the panel's one
    // "Rejected by …, … — <reason>" line already states (owner 2026-10-19:
    // "the reason is also in activity history"), so the step keeps its red
    // Rejected mark + date and drops the repeat. Approve notes stay.
    notes: (autoConfirmed || raw === 'rejected') ? null : (step.notes || null),
    mismatch,
    testId: `approval-step-${step.step_order}`,
    mdTestId: `approval-step-md-${step.step_order}`,
  };
}

/**
 * Ordered/Delivered \u2014 order-level lifecycle stages, never approval steps.
 * `statusBeforeCancel` and `receivedCount`/`totalCount` are optional; see the
 * file docblock above for exactly what each drives.
 */
function buildOrderedStage({ orderStatus, statusBeforeCancel }) {
  let displayStatus;
  const REACHED_ORDERED = ['ordered', 'partially_delivered', 'delivered'];
  if (orderStatus === 'cancelled') {
    if (statusBeforeCancel === 'approved') displayStatus = 'cancelled';
    else if (REACHED_ORDERED.includes(statusBeforeCancel)) displayStatus = 'done';
    else displayStatus = 'not_reached';
  } else if (REACHED_ORDERED.includes(orderStatus)) {
    displayStatus = 'done';
  } else if (orderStatus === 'approved') {
    displayStatus = 'awaiting';
  } else {
    displayStatus = 'not_reached'; // submitted, rejected
  }
  return {
    key: 'stage-ordered',
    kind: 'lifecycle',
    displayStatus,
    primary: 'Ordered',
    secondary: null,
    isCurrent: displayStatus === 'awaiting',
    testId: 'approval-stage-ordered',
    mdTestId: 'approval-stage-md-ordered',
  };
}

function buildDeliveredStage({ orderStatus, statusBeforeCancel, receivedCount, totalCount }) {
  let displayStatus;
  let secondary = null;
  if (orderStatus === 'cancelled') {
    if (statusBeforeCancel === 'ordered' || statusBeforeCancel === 'partially_delivered') displayStatus = 'cancelled';
    else if (statusBeforeCancel === 'delivered') displayStatus = 'done';
    else displayStatus = 'not_reached';
  } else if (orderStatus === 'delivered') {
    displayStatus = 'done';
  } else if (orderStatus === 'partially_delivered') {
    displayStatus = 'partial';
    if (Number.isFinite(totalCount) && totalCount > 0) secondary = `${receivedCount} of ${totalCount} received`;
  } else if (orderStatus === 'ordered') {
    displayStatus = 'awaiting';
  } else {
    displayStatus = 'not_reached'; // submitted, approved, rejected
  }
  return {
    key: 'stage-delivered',
    kind: 'lifecycle',
    displayStatus,
    primary: 'Delivered',
    secondary,
    isCurrent: displayStatus === 'awaiting',
    testId: 'approval-stage-delivered',
    mdTestId: 'approval-stage-md-delivered',
  };
}

/**
 * THE single view-model builder \u2014 call ONCE per render, feed the result to
 * both the mobile and the desktop branch. Never recompute per-branch.
 */
function buildLifecycleEntries(approval, { orderStatus, statusBeforeCancel, items }) {
  const steps = Array.isArray(approval?.steps) ? approval.steps : [];
  const isLive = orderStatus === 'submitted';
  const isDying = orderStatus === 'cancelled' && statusBeforeCancel === 'submitted';
  const currentStepOrder = approval?.current_step;

  const stepEntries = steps.map((step) => deriveStepView(step, { isLive, isDying, currentStepOrder }));

  // ⚖️ GOODS ONLY (owner ruling 2026-09-19). These two drive the Delivered
  // stage's "N of M" in the lifecycle timeline — a statement about what
  // arrived, so an adjustment line is outside both halves of it.
  const { received: receivedCount, total: totalCount } = countDelivery(items);

  return [
    ...stepEntries,
    buildOrderedStage({ orderStatus, statusBeforeCancel }),
    buildDeliveredStage({ orderStatus, statusBeforeCancel, receivedCount, totalCount }),
  ];
}

/**
 * The approval stepper. Fetches `/api/approvals/orders/:id` ITSELF, on mount.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DENSITY PASS, 2026-08-10 — A "TRY IT" CHANGE, HIS TO REVISE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Product owner: *"I want to tide up the history log on all orders … it way to
 * chunky and takes up a lot of room. I think we could greatly reduce the padding
 * etc and make it more compact like the vertical hieght of each row on the
 * breakdown page"*, then, relaxing the target: *"it doesnt have to match the
 * vertical height but just nice to get closer than where we are. also removing
 * unecesarry padding/boarders etc would help"*.
 *
 * 📏 MEASURED IN CHROMIUM against a real build and a copy of the dev database
 * (order 2341 — one approved step, one current, one pending), at 1024/1280/1440
 * in both themes; every combination gave identical numbers.
 *
 *      per step        60px decided / 50px pending + 8px gap → 32px / 33px, no gap
 *      whole block     262px → 146px in the expanded row   (−116px, −44%)
 *                      430px → 326px on the full order page (−104px, −24%)
 *      margin above    28px → 12px (panel) · 32px → 16px (order page)
 *
 * The Gag Breakdown's table row measures **46px** on the same run. That was the
 * original target and is now a reference; a step row is one line of text where a
 * Breakdown row carries pills and a progress bar, so 32px is under it rather than
 * level with it.
 *
 * 🔑 NOTHING WAS REMOVED FROM THE CONTENT. Every step still states its position,
 * the person, their role, the outcome, the timestamp where one exists, and which
 * step is current. What went is chrome: the status-tinted outer fill, three
 * nested per-step card borders, and one duplicated top margin. See the comments
 * at each site for which rules were kept and why.
 *
 * ⚠️ THE ACTIONS BRANCH BELOW IS BYTE-IDENTICAL — no gate, no handler, no button
 * class was touched. The one thing the density pass changes about it is its
 * INSET: it now sits inside `p-2.5` rather than `p-4`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * `showActions` — DEFAULT `true`, SO `pages/OrderPage.js` IS BYTE-IDENTICAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Added 2026-08-10 when the stepper was brought into the expanded order row on
 * Department Orders / To Approve. There it renders as a PROGRESS DISPLAY: the
 * step timeline and the override log, and none of the three controls below.
 *
 * 🚨 THIS IS NOT "the panel already has Approve/Reject, so hide the duplicate".
 * That is true and is the smaller half. The binding reason is that ALL THREE
 * handlers in this file end in a full page reload — `handleApprove` (:76),
 * `handleReject` (:104), `handleOverride` (:135) each call
 * `window.location.reload()`. Inside an expanded row that DISCARDS THE EXPANDED
 * STATE AND ANY IN-PROGRESS INLINE EDIT — precisely the loss
 * `hooks/usePollingRefresh.js`'s `paused` flag and
 * the dirty-edit guards on the order tables exist to prevent, and precisely
 * the complaint that produced the save/discard prompt in `OrderInlineEditor`.
 * A reload is survivable on a dedicated page whose whole content is one order;
 * it is data loss in a row. **Never render this component's actions inside a
 * list panel, whatever the duplication situation happens to be.**
 *
 * 🔑 AND THE PANEL'S OWN Approve/Reject ARE STRICTLY BETTER, WHICH IS WHY THIS
 * IS A SUPPRESSION AND NOT A SACRIFICE. They honour the product owner's
 * save-then-act ruling (*"approve butotn should approve and save the current
 * state"*) through `OrderInlineEditor`'s `saveRequestRef`, so a failed save
 * cannot act; these bypass the form entirely. Their `isCurrentApprover` also
 * folds in `|| isAdmin` (:147) and `canOverride` reads `capabilities` for the
 * department the viewer is STANDING IN rather than the ORDER'S OWN (:39) —
 * both looser than the panel's `useOrderAuthority().canManageOrder(order)`.
 *
 * ✅ OVERRIDE STAYS ON THE FULL ORDER PAGE — A PRODUCT RULING, NOT AN OMISSION.
 * **Override ("Skip remaining approvals and approve now", the button at :356,
 * gated by `canOverride` at :86 inside the `showActions` block at :287) has no
 * counterpart in the inline panel, so with `showActions={false}` it is
 * reachable ONLY from `/orders/:id`.** That was put to the product owner on
 * 2026-08-10 and he ruled: *"keep the override button on the full order page
 * for now and dont delete the page just yet."* 🚨 **So do NOT "restore" it to
 * the panel as a missing feature** — skipping the remaining approval steps is
 * not something that should arrive as a side effect of a layout change, and the
 * control as written reloads the page. If it is ever wanted inline it needs a
 * fresh implementation that does not reload, plus a gate resolved in the
 * ORDER'S OWN department rather than the viewer's active one.
 * ⚠️ It is also the first thing that would have to move if `/orders/:id` were
 * ever retired — see the retirement assessment.
 */
function ApprovalStatus({
  orderId, orderStatus, showActions = true,
  // Order-lifecycle stepper additions (2026-08-18) — both OPTIONAL and both
  // "data already fetched by the caller", never a new request from here.
  // `items` (order.items, for the Delivered stage's "N of M received");
  // `statusBeforeCancel` (order.status_before_cancel, for deciding WHICH
  // stage a cancelled order died at). Omitting either is safe: no items ⇒
  // no "N of M received" text; no statusBeforeCancel ⇒ no red marker is
  // fabricated anywhere, see `buildLifecycleEntries`'s docblock.
  items = [], statusBeforeCancel = null,
}) {
  const [approval, setApproval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  // 📱 PHONE (owner 2026-10-03): Order Progress is a COLLAPSED section like the
  // others; the status badge stays on the header. Desktop always shows it.
  const [phoneOpen, setPhoneOpen] = useState(false);
  // 🖥️ Desktop folds it too, OPEN by default (owner 2026-10-10: every section
  // "in a similar style to the next"; the desktop rule is "Foldable, open").
  // One click flips both: each width only ever shows its own half.
  const [desktopOpen, setDesktopOpen] = useState(true);
  const isDesktopWidth = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
  // Approve-on-behalf (2026-08-18 owner ruling) — see
  // `ApproveOnBehalfDialog.js`'s docblock. This component's own gate admits
  // exactly one "not the named approver" caller: a global admin viewing
  // someone else's run (`isCurrentApprover` below folds in `|| isAdmin`).
  const [pendingApproveConfirm, setPendingApproveConfirm] = useState(false);
  // 🔀 2026-09-14 — the dialog's MANDATORY reason (owner: approve on behalf
  // *"is still an override"*). Controlled textarea, so the state lives here.
  const [approveOnBehalfReason, setApproveOnBehalfReason] = useState('');

  const userName = localStorage.getItem('sfx_requested_by') || '';
  const userId = parseInt(localStorage.getItem('sfx_user_id'), 10) || null;
  const { isAdmin, capabilities } = useDepartment();

  // Who sees the Override control. Mirrors the server gate in
  // server/routes/approvals.js (`req.isGlobalAdmin ||
  // canManageDepartmentSettings(req, order.department_id)`) so the button is not
  // offered to somebody who would only get a 403.
  //
  // ⚠️ IT IS NOT THE SAME QUESTION, and must never be mistaken for the gate.
  // `capabilities` is resolved for the department the user is STANDING IN, and
  // the server resolves it for the ORDER'S OWN department. Those coincide on
  // every ordinary path, and where they do not the server refuses. This is a UI
  // convenience; the server is what enforces any of it.
  const canOverride = isAdmin || capabilities.canManageDepartmentSettings === true;

  const fetchApproval = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch(`/api/approvals/orders/${orderId}`);
      if (res.ok) {
        const data = await res.json();
        setApproval(data);
      }
    } catch (err) {
      console.error('Failed to fetch approval status:', err);
    } finally {
      setLoading(false);
    }
  }, [orderId, userName, isAdmin]);

  useEffect(() => {
    fetchApproval();
  }, [fetchApproval]);

  // The actual mutation — unchanged, just renamed so `handleApprove` can be
  // the gate below. Called either directly (the ordinary "it's your step"
  // path) or from the approve-on-behalf dialog's Confirm.
  // 🔀 `onBehalfReason` (2026-09-14) is passed ONLY by the dialog's Confirm;
  // the own-step path calls this with nothing and its body is unchanged.
  const performApprove = async (onBehalfReason) => {
    try {
      setActionLoading(true);
      const res = await apiFetch(`/api/approvals/orders/${orderId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes || null, ...(onBehalfReason ? { reason: onBehalfReason } : {}) })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to approve');
      }
      setNotes('');
      setActionMessage({ type: 'success', text: 'Step approved successfully!' });
      setTimeout(() => setActionMessage(null), 3000);
      fetchApproval();
      // Reload order to update status
      if (window.location.reload) setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  // The approve-on-behalf gate (2026-08-18). References `currentStep`, the
  // const computed further down this render before the button that calls
  // this is ever reached — see this component's docblock for why that
  // closure is safe: whenever the button is visible, THIS render already
  // computed `currentStep` before returning its JSX, and each render binds
  // its own `handleApprove` to its own `currentStep`. Compared on
  // `approver_user_id`, never on the name — `utils/orderStepInfo.js#isDifferentApprover`,
  // the same predicate `pages/DepartmentOrders.js` uses.
  const handleApprove = async () => {
    if (currentStep && isDifferentApprover(currentStep.approver_user_id, userId)) {
      setApproveOnBehalfReason('');
      setPendingApproveConfirm(true);
      return;
    }
    await performApprove();
  };

  const handleReject = async () => {
    if (!notes.trim()) {
      setActionMessage({ type: 'error', text: 'Please provide a reason for rejection' });
      return;
    }
    try {
      setActionLoading(true);
      const res = await apiFetch(`/api/approvals/orders/${orderId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes.trim() })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to reject');
      }
      setNotes('');
      setShowRejectForm(false);
      setActionMessage({ type: 'success', text: 'Order rejected.' });
      setTimeout(() => setActionMessage(null), 3000);
      fetchApproval();
      if (window.location.reload) setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOverride = async () => {
    // The server requires a reason from EVERY overrider, the site admin
    // included (an override bypasses a control and approval_override_log is the
    // compensating one). Same shape as handleReject above; the server refuses
    // regardless.
    // 🔻 **WAS `if (!isAdmin && !overrideReason.trim())` UNTIL 2026-09-13.**
    // Owner: *"the override should require a reason"*. The admin exemption came
    // out of `POST /api/approvals/orders/:id/override` in the same commit, so
    // leaving it here would have let this page fire a request it knows will
    // come back 400. Nothing else on this page changes — it is being retired.
    if (!overrideReason.trim()) {
      setActionMessage({ type: 'error', text: 'Please provide a reason for the override' });
      return;
    }
    try {
      setActionLoading(true);
      const res = await apiFetch(`/api/approvals/orders/${orderId}/override`, {
        method: 'POST',
        body: JSON.stringify({ reason: overrideReason.trim() })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to override');
      }
      setOverrideReason('');
      setShowOverrideForm(false);
      setActionMessage({ type: 'success', text: 'Approval overridden - order approved!' });
      setTimeout(() => setActionMessage(null), 3000);
      fetchApproval();
      if (window.location.reload) setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return null;
  if (!approval || !approval.has_workflow) return null;

  const currentStep = approval.steps?.find(s => s.step_order === approval.current_step);
  const isCurrentApprover = currentStep && (currentStep.approver_user_id === userId || isAdmin);
  // How many sequential same-approver steps ONE press of Approve would decide
  // from here (2026-08-18 owner ruling) — a DISPLAY convenience computed over
  // the steps this component already fetched; the server is the sole
  // authority on what actually collapses. See utils/approvalStepCollapse.js.
  const collapsibleCount = currentStep
    ? collapsibleStepCountFrom(approval.steps || [], approval.current_step)
    : 1;
  // M3 (2026-08-18 adversarial review): "Approve all N of YOUR steps" is only
  // true when the viewer IS the step's named approver. Unlike Approvals.js's
  // To Approve list (whose server query already filters to that case), this
  // component is also reachable by a GLOBAL ADMIN viewing SOMEONE ELSE's run
  // — `isCurrentApprover` above (pre-existing, untouched) gates actions on
  // `currentStep.approver_user_id === userId || isAdmin` alone, with no
  // department-capability disjunct, so admin is the only "not the named
  // approver" case reachable here. The button must then attribute the steps
  // to the person who actually holds them, not to the admin pressing it.
  const isViewerNamedApprover = !!(currentStep && currentStep.approver_user_id === userId);
  const isPending = approval.overall_status === 'pending';
  // `isAtHiddenStep` NOW GOVERNS ONLY THE ACTIONS BLOCK'S COPY (button label,
  // "ready to place" prompt) — never step-status display any more. See
  // `buildLifecycleEntries`'s docblock above for why the old compensation
  // that ALSO lived off this flag was deleted rather than narrowed.
  const isAtHiddenStep = currentStep && currentStep.is_hidden;
  // The final (once-hidden) step is now RENDERED, so the total is the WHOLE
  // chain, never `visible_total_steps` (which the server computes by
  // excluding it) and never clamped against it.
  const totalStepsCount = approval.total_steps || approval.steps?.length || 0;
  const displayCurrentStep = approval.current_step;

  const overallConfig = STATUS_CONFIG[approval.overall_status] || STATUS_CONFIG.pending;
  // THE single view-model computation — fed to both the mobile and the
  // desktop render branch below, never recomputed per-branch.
  const lifecycleEntries = buildLifecycleEntries(approval, { orderStatus, statusBeforeCancel, items });

  return (
    /* DENSITY, 2026-08-10 — product owner: the history log is *"way to chunky and
       takes up a lot of room ... we could greatly reduce the padding etc"*, and,
       on the follow-up, *"removing unecesarry padding/boarders etc would help"*.
       Nothing was removed from the CONTENT; every step still states its position,
       person, role, outcome, timestamp and whether it is current.

       🔴 NO `mt-4` ANY MORE, AND ITS ABSENCE IS A FIX RATHER THAN A CUT: BOTH
       callers already supply the top margin, so the old one was DOUBLED on both
       surfaces — `pages/OrderPage.js:1482` wraps this in `<div className="mt-4">`
       (measured 32px of gap above the block) and `components/OrderExpandedDetails.js`
       renders it inside a `mt-3 space-y-3` wrapper. Removing it costs no caller
       an edit and leaves each with exactly the margin it asked for.
       ⚠️ **CORRECTED 2026-08-11.** This said the panel renders it "as the FIRST
       CHILD of a `mt-3 space-y-3` wrapper (measured 28px)" — the position is no
       longer true and the measurement was a consequence of it. The stepper moved
       to LAST in that panel, below activity history (product owner: *"i feel
       like approval workflow should be below activity history?"*), so its top
       margin now comes from the wrapper's `space-y-3` rather than from `mt-3`.
       🔑 The CLAIM this paragraph makes is unaffected — both callers still
       supply a margin and re-adding `mt-4` would still double it on both — which
       is exactly why the position had to be corrected rather than the conclusion
       re-derived: a stale premise under a still-true conclusion is the shape
       CLAUDE.md warns about, and it reads as verified.

       🔴 THE OUTER CARD IS NEUTRAL NOW — no status tint, no status-coloured edge.
       It kept a plain hairline because on the FULL ORDER PAGE this is the only
       edge the block has: every neighbouring section there is a
       `bg-white dark:bg-gray-800 rounded-lg shadow-md` card (OrderPage.js:1122,
       :1170, :1235, :1411, :1493), so a borderless block would float. What went
       is the whole-block `bg-yellow-100` / `border-yellow-300` fill keyed to
       overall status — a third and fourth way of saying what the badge two
       inches away already says in words.
       🪤 `border-gray-200` is BARE on purpose: `index.css:273` sets
       `html.dark .border-gray-200 { border-color:#334155 !important }`, so a
       local `dark:border-gray-700` here would be inert. */
    <div className={`border border-gray-200 rounded-lg p-2.5 md:border-0 ${DESKTOP_SECTION_CARD}`} data-testid="approval-status">
      <div
        className={`flex items-center justify-between gap-2 mb-1.5 cursor-pointer${phoneOpen ? '' : ' max-md:mb-0'}${desktopOpen ? '' : ' md:mb-0'} ${DESKTOP_SECTION_HEAD_BLEED}`}
        data-phone-section-head=""
        onClick={() => { setPhoneOpen((o) => !o); setDesktopOpen((o) => !o); }}
        aria-expanded={isDesktopWidth ? desktopOpen : phoneOpen}
        data-testid="approval-status-toggle"
      >
        <h3 className={`font-semibold text-sm text-gray-800 flex items-center gap-1.5 md:gap-2 ${DESKTOP_SECTION_TITLE}`}>
          <svg data-chevron xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 ${DESKTOP_SECTION_CHEVRON} shrink-0 text-gray-400 transition-transform${phoneOpen ? '' : ' max-md:-rotate-90'}${desktopOpen ? '' : ' md:-rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
          <svg className="md:hidden w-4 h-4 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Order Progress
        </h3>
        {/* 🔴 NEVER SAYS "Approved" WHILE THE ORDER IS STILL PENDING — this is
            the header half of the compensation rework (see
            `buildLifecycleEntries`'s docblock). It used to render a green
            "Approved - Ready to Place" pill while sitting at the still-`pending`
            final step; the colour AND the label always come from the REAL
            `overall_status` now. `isAtHiddenStep` earns its keep only as a
            nicer LABEL for the same true state — "Awaiting placement" instead
            of the generic "Pending" — never as a claim that anything is done. */}
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${overallConfig.bg} ${overallConfig.text}`}>
          {isPending && isAtHiddenStep ? 'Awaiting placement' : overallConfig.label}
          {isPending && ` (Step ${displayCurrentStep}/${totalStepsCount})`}
        </span>
      </div>

      <div className={`md:mt-2 ${phoneOpen ? '' : 'max-md:hidden'}${desktopOpen ? '' : ' md:hidden'}`} data-testid="approval-status-body">
      {/* Action Messages */}
      {actionMessage && (
        <div className={`mb-3 px-3 py-2 rounded text-sm ${actionMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {actionMessage.text}
        </div>
      )}

      {/* ═══ Steps timeline (visible steps only) ═══
          ONE LINE PER STEP. The old shape was three bordered, filled cards —
          each `p-2.5 rounded-lg border` — stacked with `space-y-2` INSIDE the
          bordered card above, i.e. two levels of box, and it put the timestamp
          on a line of its own. Measured before: 60px for a decided step, 50px
          for a pending one, plus an 8px gap between each.

          🔑 WHAT THE HAIRLINE REPLACED, AND WHY THAT ONE STAYS. A rule between
          two steps is doing real work — it is what says where one person's entry
          ends and the next begins — so it survives as a 1px `divide-y` instead
          of 22px of per-step border-and-padding. The per-step BOX, by contrast,
          was saying "this is a card" inside something that already is one.

          🚨 THE CURRENT STEP KEEPS THREE OF ITS FOUR SIGNALS, WHICH IS THE POINT.
          It had four: the blue card border, the `bg-blue-50` fill, the ring on
          the number, and the word "Current". Only the BORDER is gone; the fill,
          the ring and the word all remain — and the word is TEXT, so colour is
          not left as a sole carrier and WCAG 1.4.11's 3:1 non-text minimum is
          not newly invoked (the trap recorded against `components/DueIndicator.js`,
          where dropping one of two redundant signals silently promoted the
          survivor). A DECIDED step likewise keeps its glyph AND its word; only
          the pill's background fill went, so "Approved" is still readable with
          no colour perception at all.
          🪤 `bg-blue-50` is BARE deliberately — `index.css:440` sets
          `html.dark .bg-blue-50 { background-color: rgba(59,130,246,0.1) !important }`,
          so a local `dark:bg-blue-900/20` here would be compiled and inert.
          `divide-gray-100` is the opposite case: index.css overrides only
          `.divide-gray-200 > …` (:290), so this pair is live and is written.

          ═══ HORIZONTAL DESKTOP LAYOUT, 2026-08-15 — A PROTOTYPE, HIS TO DECLINE ═══
          Product owner: *"Approval Workflow, can we try putting it in a vertical
          line for desk top view so its not taking up as much vertical space"* —
          the goal clause is the binding half: steps side by side in ONE ROW from
          `md:` up, so a 3-step chain costs one row's height instead of three.

          TWO BRANCHES, NOT RESPONSIVE RESTRUCTURING, on purpose: the stacked
          list below is BYTE-IDENTICAL to the pre-change markup (its container
          only gained `md:hidden`), so the mobile rendering cannot drift and
          every existing `approval-step-N` assertion still addresses it. The
          desktop row is a sibling with its own `approval-step-md-N` testids.
          The per-step computation is hoisted into `renderSteps` so the two
          branches CANNOT disagree about status/current/hidden-step handling —
          one computation, two renderings (the copy-instead-of-a-call trap).

          LONG CHAINS WRAP (`flex-wrap`); they do not scroll. A scrollport hides
          steps — the point of the chain is seeing it whole — and the expanded
          row already sits inside a measured scrollport, so nesting a second
          horizontal scroll context there is worse than a second line. A 6+ step
          chain becomes two rows of ~46px, still under half its stacked height.

          NO CONTENT REMOVED (the compaction ruling): every step still shows the
          bubble, name, role, outcome word, timestamp, the mismatch "Approved
          by", the note, and the WORD "Current" (text channel — colour is never
          the sole carrier). Role + timestamp keep the exact PAIR
          `text-xs text-gray-400` in BOTH branches — `index.css`'s print block
          (:786-790) hides `.text-xs.text-gray-400`, so re-shading either would
          change what a printed page shows. The connector is a hairline div in
          `border-gray-200` (index.css:273 delivers its dark value; a `dark:`
          pair here would be inert). The desktop branch deliberately carries NO
          `dark:` tokens at all — every colour in it is in index.css's override
          table. The override log below and the `showActions` branch are
          untouched. */}
      {/* Mobile / below-md: the stacked list, byte-identical CONTAINER markup.
          Rows now come from `lifecycleEntries` (approval steps, including the
          once-hidden final one, PLUS the Ordered/Delivered lifecycle stages)
          rather than from a filtered `visibleSteps` — one computation, fed to
          this branch AND the desktop one below, so they cannot disagree. */}
      <div className="divide-y divide-gray-100 dark:divide-gray-700 md:hidden">
        {lifecycleEntries.map((entry) => {
          const config = STATUS_CONFIG[entry.displayStatus] || STATUS_CONFIG.not_reached;
          // The step's own NUMBER while awaiting/not-yet-reached, the outcome
          // GLYPH once decided — never the only thing saying either. Lifecycle
          // stages (Ordered/Delivered) have no step number, so they always
          // show the glyph.
          const showNumber = entry.kind === 'approval'
            && (entry.displayStatus === 'awaiting' || entry.displayStatus === 'not_reached');
          return (
            /* ⚠️ `flex-wrap`, never `truncate`, on the identity items. A long
               approver name must WRAP rather than be clipped — the constraint on
               this change is that no information is lost, and an ellipsed name is
               lost information on the one surface that says who approved what. */
            <div key={entry.key}
              className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 px-1.5 py-1.5 ${entry.isCurrent ? 'bg-blue-50' : ''}`}
              data-testid={entry.testId}>

              <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold leading-none ${config.bg} ${config.text} ${entry.isCurrent ? 'ring-1 ring-blue-400' : ''}`}>
                {showNumber ? entry.stepOrder : config.icon}
              </span>

              <span className="font-medium text-sm text-gray-800">{entry.primary}</span>
              {/* 🪤 `text-xs text-gray-400` is kept as a PAIR on this and on the
                  timestamp below, unchanged from the old markup, because
                  `index.css`'s print block (:786-790) hides `.text-xs.text-gray-400`
                  outright. Re-shading either would silently change what a printed
                  order page shows — a behaviour change wearing a tidy-up's clothes.
                  An approval step shows the ROLE here; a lifecycle stage shows its
                  own SECONDARY text (only the Delivered stage's "N of M received"
                  ever sets one). */}
              {entry.kind === 'approval' ? (
                <>
                  <span className="text-xs text-gray-400">({entry.roleLabel})</span>
                  {entry.autoConfirmed && <span className="text-xs text-gray-400">confirmed automatically</span>}
                </>
              ) : entry.secondary ? (
                <span className="text-xs text-gray-400">{entry.secondary}</span>
              ) : null}
              {/* Right-aligned (owner 2026-10-02): the outcome word starts the
                  row's right-hand cluster; the timestamp follows it. */}
              <span className={`text-xs font-medium ml-auto ${config.text}`} data-testid={`${entry.testId}-status`}>{config.label}</span>
              {/* WHO ACTUALLY approved, when that is NOT the person the step
                  names. `approver_name` above is who the step was assigned to;
                  `approved_by_user_id` is who consumed it (a manager holding
                  canManageDepartmentOrders, or a global admin, may approve a
                  step named for someone else). Showing "Approved by <actual>"
                  stops the ✓ Approved reading as a sign-off the named person
                  gave (M3.2). Keyed on the id, never the name — display names
                  are not unique — and shown only on a REAL mismatch, so a
                  self-approval adds nothing. `approved_by_name` is resolved
                  server-side from the id; if the actor was later deleted the id
                  is NULL and the clause is simply absent. */}
              {entry.mismatch && (
                <span className="text-xs text-gray-500">
                  Approved by <span className="font-medium text-gray-700">{entry.mismatch}</span>
                </span>
              )}
              {entry.isCurrent && <span className="text-xs text-blue-600 font-medium animate-pulse">Current</span>}
              {entry.timestamp && (
                /* Was a line of its own at `7 May 2026, 01:00`; now shares the row
                   and drops to a two-digit year. The TIME is information and stays
                   — see `formatDateTimeCompact`, which exists because
                   `formatDateCompact` strips it. Lifecycle stages never carry
                   one — see `buildLifecycleEntries`'s docblock for why. */
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {formatDateTimeCompact(entry.timestamp)}
                </span>
              )}
              {entry.notes && (
                /* `w-full` forces a second line for a note, and only when one
                   exists — a note is free text of any length and must not be
                   squeezed onto the identity row. */
                <p className="w-full text-xs text-gray-500">Note: {entry.notes}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop / md-up: the same entries in ONE horizontal row. Each is a
          compact two-line cell — bubble + name + outcome (+ "Current") on the
          first line, role/secondary + timestamp beneath — with a hairline
          connector between entries. Wraps rather than scrolls (see the block
          comment). */}
      <div className="hidden md:flex md:flex-wrap items-stretch gap-y-1" data-testid="approval-steps-row">
        {lifecycleEntries.map((entry, i) => {
          const config = STATUS_CONFIG[entry.displayStatus] || STATUS_CONFIG.not_reached;
          const showNumber = entry.kind === 'approval'
            && (entry.displayStatus === 'awaiting' || entry.displayStatus === 'not_reached');
          return (
            <React.Fragment key={entry.key}>
              {i > 0 && (
                <div className="w-4 mx-1 self-center border-t border-gray-200 flex-shrink-0" aria-hidden="true" />
              )}
              <div
                className={`flex items-start gap-1.5 px-1.5 py-1 rounded ${entry.isCurrent ? 'bg-blue-50' : ''}`}
                data-testid={entry.mdTestId}>
                <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold leading-none ${config.bg} ${config.text} ${entry.isCurrent ? 'ring-1 ring-blue-400' : ''}`}>
                  {showNumber ? entry.stepOrder : config.icon}
                </span>
                <span className="flex flex-col min-w-0">
                  <span className="flex flex-wrap items-baseline gap-x-1.5">
                    {/* flex-wrap, never truncate — a long approver name must WRAP;
                        an ellipsed name is lost information on the one surface
                        that says who approved what (same rule as the stacked row). */}
                    <span className="font-medium text-sm text-gray-800">{entry.primary}</span>
                    <span className={`text-xs font-medium ${config.text}`}>{config.label}</span>
                    {entry.isCurrent && <span className="text-xs text-blue-600 font-medium animate-pulse">Current</span>}
                  </span>
                  <span className="flex flex-wrap items-baseline gap-x-1.5">
                    {/* 🪤 `text-xs text-gray-400` kept as a PAIR on role/secondary and
                        timestamp — the print block hides that pair; see above. */}
                    {entry.kind === 'approval' ? (
                      <>
                        <span className="text-xs text-gray-400">({entry.roleLabel})</span>
                        {entry.autoConfirmed && <span className="text-xs text-gray-400">confirmed automatically</span>}
                      </>
                    ) : entry.secondary ? (
                      <span className="text-xs text-gray-400">{entry.secondary}</span>
                    ) : null}
                    {entry.timestamp && (
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatDateTimeCompact(entry.timestamp)}
                      </span>
                    )}
                  </span>
                  {entry.mismatch && (
                    <span className="text-xs text-gray-500">
                      Approved by <span className="font-medium text-gray-700">{entry.mismatch}</span>
                    </span>
                  )}
                  {entry.notes && (
                    /* max-w keeps a free-text note from blowing the cell wide;
                       it wraps inside the step's own column instead. */
                    <span className="text-xs text-gray-500 max-w-[16rem]">Note: {entry.notes}</span>
                  )}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* ═══ Override log ═══
          🔴 THIS BORDER AND FILL ARE KEPT, DELIBERATELY, AND THAT IS AS MUCH A
          DECISION AS THE ONES REMOVED ABOVE. An override means somebody SKIPPED
          the remaining approvals — an exceptional act, and the callout is its
          only carrier anywhere in this component. Stripping it to a hairline
          would make a bypass of the control read like an ordinary row. Only the
          padding came down (`p-3` → `p-2`, `mb-4` → `mt-2`).
          ⚠️ Its dark pairs are NEW and they genuinely win: index.css has no rule
          for `bg-orange-50`, `border-orange-200` or `text-orange-700` (the only
          orange on that axis is the compound `[data-section-title].bg-orange-50`
          at :205), so this block was rendering as a bright cream card with dark
          orange text on a dark page. The date span already carried
          `dark:text-orange-400`, so the intent was there and half-applied. */}
      {approval.overrides && approval.overrides.length > 0 && (
        <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700/40 rounded">
          <p className="text-xs font-medium text-orange-700 dark:text-orange-300 mb-0.5">Override Log:</p>
          {approval.overrides.map((o, i) => (
            <p key={i} className="text-xs text-orange-600 dark:text-orange-400">
              Overridden by <span className="font-semibold">{o.overridden_by}</span> at step {o.previous_step}.
              {o.reason && <span> Reason: {o.reason}</span>}
              <span className="text-orange-600 dark:text-orange-400 ml-1">({new Date(o.created_at).toLocaleString('en-GB')})</span>
            </p>
          ))}
        </div>
      )}

      {/* Actions for the current approver.
          🚨 `showActions` gates ALL THREE controls (Approve, Reject, Override),
          not just the two that duplicate the panel's — see the component
          docblock: each handler ends in `window.location.reload()`, which in an
          expanded row destroys the row and any unsaved edit. */}
      {showActions && isPending && isCurrentApprover && (
        <div className="border-t border-gray-200 pt-3 mt-3" data-testid="approval-actions">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {isAtHiddenStep
              ? 'All approvals complete. This order is ready for you to place:'
              : currentStep.approver_user_id === userId
                ? 'This order is waiting for your approval:'
                : `Acting as admin for ${currentStep.approver_name}:`
            }
          </p>

          {!showRejectForm && (
            <>
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="Add a note (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="approval-notes-input"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={handleApprove} disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  data-testid="approve-btn">
                  {actionLoading
                    ? 'Processing...'
                    : collapsibleCount > 1
                      ? approveButtonLabel(collapsibleCount, isAtHiddenStep, {
                          isViewerNamedApprover,
                          approverName: currentStep?.approver_name,
                        })
                      : isAtHiddenStep ? 'Confirm & Place Order' : `Approve (Step ${displayCurrentStep})`}
                </button>
                <button onClick={() => setShowRejectForm(true)} disabled={actionLoading}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  data-testid="reject-btn">
                  Reject
                </button>
              </div>
            </>
          )}

          {showRejectForm && (
            <div className="bg-red-50 p-3 rounded-lg border border-red-200">
              <p className="text-sm text-red-700 font-medium mb-2">Reason for rejection:</p>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full border border-red-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                rows={2}
                placeholder="Enter rejection reason..."
                data-testid="rejection-reason-input"
              />
              <div className="flex gap-2 mt-2">
                <button onClick={handleReject} disabled={actionLoading}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-3 py-1.5 rounded text-sm font-medium">
                  Confirm Rejection
                </button>
                <button onClick={() => { setShowRejectForm(false); setNotes(''); }}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm font-medium">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Override — global admin, or a buyer/supervisor of this department */}
          {canOverride && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              {!showOverrideForm ? (
                <button onClick={() => setShowOverrideForm(true)}
                  className="text-orange-600 hover:text-orange-800 text-xs font-medium underline"
                  data-testid="override-btn">
                  Override: Skip remaining approvals and approve now
                </button>
              ) : (
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-700 font-medium mb-2">Override Reason (logged):</p>
                  <input
                    type="text"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    className="w-full border border-orange-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Enter reason for override..."
                    data-testid="override-reason-input"
                  />
                  <div className="flex gap-2 mt-2">
                    <button onClick={handleOverride} disabled={actionLoading}
                      className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-3 py-1.5 rounded text-sm font-medium">
                      Confirm Override
                    </button>
                    <button onClick={() => { setShowOverrideForm(false); setOverrideReason(''); }}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm font-medium">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>

      {/* Approve-on-behalf (2026-08-18) — see this file's `handleApprove`
          and `ApproveOnBehalfDialog.js`'s docblock. Cancel writes nothing;
          Confirm reads `currentStep`/`collapsibleCount` from this render's
          closure and calls `performApprove` directly, never `handleApprove`
          again (which would just re-open this same dialog). */}
      <ApproveOnBehalfDialog
        open={pendingApproveConfirm}
        approverName={currentStep?.approver_name}
        stepCount={collapsibleCount}
        reason={approveOnBehalfReason}
        onReasonChange={setApproveOnBehalfReason}
        busy={actionLoading}
        onCancel={() => { setPendingApproveConfirm(false); setApproveOnBehalfReason(''); }}
        onConfirm={(reason) => { setPendingApproveConfirm(false); setApproveOnBehalfReason(''); performApprove(reason); }}
      />
    </div>
  );
}

export default ApprovalStatus;
